/**
 * Ports with Landing data layer - checkbox + description. Fetches ports when layer is on.
 * Syncs from layout so layer shows when provided in initialLayers even before tab is opened.
 * Only clears map data when transitioning from active to inactive (not on mount with isActive false).
 */
import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import type { RegistryComponentProps } from '../../../pages/registry/componentRegistry';
import { useDataLayer } from '../hooks/useDataLayer';
import { useApi } from '../../../api/api';

export default function PortsWithLandingDataLayer(
  props: RegistryComponentProps
): React.ReactElement {
  const { slug, isReady, isActive, setActive, mapController } = useDataLayer(props);
  const { getPorts } = useApi();
  const [loading, setLoading] = useState(false);
  const wasActiveRef = useRef(false);
  const syncedFromLayoutRef = useRef(false);

  // When layer is in layout, mapController has it active but our isActive may not have synced yet.
  useLayoutEffect(() => {
    if (syncedFromLayoutRef.current || !setActive || !mapController?.isReady()) return;
    if (mapController.isLayerActive(slug) && !isActive) {
      setActive(true);
      syncedFromLayoutRef.current = true;
    }
  }, [slug, isActive, setActive, mapController]);

  useEffect(() => {
    if (!mapController || !mapController.isReady()) return;
    if (isActive) {
      wasActiveRef.current = true;
      let cancelled = false;
      setLoading(true);
      getPorts()
        .then((res) => {
          const data = res?.data ?? [];
          if (!cancelled) mapController.setPortsWithLandingData(data);
        })
        .catch((err) => {
          console.error('Error fetching ports data:', err);
          if (!cancelled) mapController.setPortsWithLandingData([]);
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
      return () => {
        cancelled = true;
      };
    }
    // Only clear when we're turning the layer off (was active, now inactive), not on initial mount
    if (wasActiveRef.current) {
      wasActiveRef.current = false;
      mapController.setPortsWithLandingData([]);
    }
  }, [isActive, mapController, getPorts]);

  return (
    <div className="control-section">
      <div className="section-title">
        <span className="section-icon">Ports with Landing Data</span>
      </div>
      <div style={{ fontSize: '12px', color: '#6c757d', marginBottom: '18px', fontStyle: 'italic' }}>
        Top 15 New England fishing ports showing 2023 commercial landings value
      </div>
      <div className="form-group">
        <label className="checkbox-label" style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={isActive}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setActive(e.target.checked)}
            disabled={!isReady}
            style={{ marginRight: '8px' }}
          />
          <span>Show Ports with Landing Data</span>
          {loading && (
            <span style={{ marginLeft: '8px', fontSize: '12px', color: '#6c757d' }}>(Loading...)</span>
          )}
        </label>
      </div>
      {isActive && (
        <div
          style={{
            marginTop: '12px',
            padding: '12px',
            backgroundColor: '#f0fdf4',
            borderRadius: '6px',
            fontSize: '12px',
            border: '1px solid #86efac',
          }}
        >
          <div style={{ fontWeight: '600', marginBottom: '8px', color: '#065f46' }}>
            Ports with Landing Data Displayed
          </div>
          <div style={{ fontSize: '11px', color: '#555', marginBottom: '8px' }}>
            Click on a port marker to see detailed landing information including:
          </div>
          <ul style={{ margin: '8px 0', paddingLeft: '20px', fontSize: '11px', color: '#555' }}>
            <li>2023 Landing Value (dollars)</li>
            <li>2023 Landing Volume (pounds)</li>
            <li>Port location and state</li>
          </ul>
          <div style={{ marginTop: '10px', display: 'flex', alignItems: 'center', fontSize: '11px', color: '#666' }}>
            <span
              style={{
                display: 'inline-block',
                width: '12px',
                height: '12px',
                borderRadius: '50%',
                backgroundColor: '#10b981',
                marginRight: '6px',
                border: '2px solid white',
                boxShadow: '0 0 0 1px rgba(0,0,0,0.2)',
              }}
            />
            <span>Fishing Port Location</span>
          </div>
          <div
            style={{
              marginTop: '10px',
              paddingTop: '8px',
              borderTop: '1px solid #d1fae5',
              fontSize: '10px',
              color: '#666',
              fontStyle: 'italic',
            }}
          >
            Data: NOAA 2023 Port Landings (Top 15 NE Ports)
          </div>
        </div>
      )}
    </div>
  );
}
